<script setup>
import { computed } from 'vue';
import Logout from '@/components/auth/Logout.vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth.js';
import IconLogin from '~icons/svg/login';

defineProps({
  isMenuOpen: Boolean,
});
const emit = defineEmits(['update:isMenuOpen']);
const router = useRouter();
const route = useRoute();
// user認証情報
const auth = useAuthStore();
// メニュー
const menuItems = computed(() => {
  return router.options.routes.filter((route) => {
    // hideInMenu が true のルートは表示しない
    if (route.meta?.hideInMenu) {
      return false;
    }
    // ログインしていない場合は表示しない
    if (!auth.isAuthenticated) {
      return false;
    }
    // 管理者専用ルートで、ユーザーが管理者でない場合は表示しない
    if (route.meta?.requiresAdmin && !auth.isAdmin) {
      return false;
    }
    // 上記以外は表示する
    return true;
  });
});

// login → signup、signup → login
const toggleAuthLink = computed(() => {
  if (route.name === 'Login') {
    return { name: 'SignUp' };
  }
  if (route.name === 'SignUp') {
    return { name: 'Login' };
  }
  return null; // それ以外のページでは表示しない
});
</script>

<template>
  <!-- 大きいデバイス -->
  <nav class="hidden md:flex flex-col items-end">
    <div class="flex items-center gap-3 mb-2 text-sm border-emerald-300 pb-1">
      <div v-if="auth.user" class="pe-2 border-r-2 border-emerald-300">
        {{ auth.user.displayName }} さんでログイン中
      </div>
      <!-- ログイン・登録ボタン -->
      <router-link v-if="!auth.user && toggleAuthLink" :to="toggleAuthLink"
        >{{ route.name === 'Login' ? '登録はこちら' : 'ログイン'
        }}<IconLogin class="text-white inline"
      /></router-link>
      <!-- ログアウトボタン -->
      <Logout v-if="auth.user" />
    </div>

    <!-- メニュー部分 -->
    <ul class="flex gap-1">
      <li v-for="route in menuItems" :key="route.path">
        <router-link
          :to="{ name: route.name }"
          active-class="font-bold"
          class="px-4 block hover:text-emerald-600 hover:bg-emerald-100 rounded transition-colors duration-200"
        >
          {{ route.meta?.title || route.name }}
        </router-link>
      </li>
    </ul>
  </nav>

  <!-- 小さいデバイス -->
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="transform -translate-y-4 opacity-0"
    enter-to-class="transform translate-y-0 opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="transform translate-y-0 opacity-100"
    leave-to-class="transform -translate-y-4 opacity-0"
  >
    <div
      v-if="isMenuOpen"
      class="md:hidden absolute top-full left-0 w-full bg-emerald-600 shadow-xl z-50"
    >
      <ul class="flex flex-col p-4 justify-items-center border-t border-emerald-400">
        <li
          v-if="auth.user"
          class="text-center text-emerald-100 text-sm border-b border-emerald-400"
        >
          {{ auth.user.displayName }} さんでログイン中
        </li>

        <li
          v-for="route in menuItems"
          :key="route.path"
          class="text-sm text-center border-b border-emerald-400 text-emerald-100"
          @click="emit('update:isMenuOpen', false)"
        >
          <router-link
            :to="{ name: route.name }"
            active-class="font-bold text-white"
            class="block py-4"
          >
            {{ route.meta?.title || route.name }}
          </router-link>
        </li>

        <li v-if="auth.user" class="text-emerald-100 text-sm py-4">
          <Logout />
        </li>
        <li v-if="!auth.user && toggleAuthLink">
          <router-link
            :to="{ name: toggleAuthLink.name }"
            class="block py-4 text-emerald-100"
            active-class="font-bold text-white"
            >{{ route.name === 'Login' ? '登録はこちら' : 'ログイン' }} <IconLogin
          /></router-link>
        </li>
      </ul>
    </div>
  </Transition>
</template>
